import {Component, TargetPosition} from "@/event/calendar/dom/Component.ts";
import {computed, ComputedRef, CSSProperties, type Ref, ref, watch, WatchStopHandle} from "vue";
import {DrawingContext, DrawingContextProperties} from "@/event/calendar/dom/DrawingContext.ts";
import {FormEnvironment} from "@fd/Types.ts";
import {Coordinates, Rect} from "@/event/calendar/Shapes.ts";
import {localCoord} from "@/event/calendar/Events.ts";
import {AnimateResult, Animation, AnimationEngine, AnimationID} from "@/event/calendar/dom/Animation.ts";
import {CustomEvent} from "@/event/calendar/dom/CustomEvent.ts";
import {DirtyFunction} from "@/store/CalendarStore.ts";

export type InvalidateHandler = (...animations:Animation[]) => void;
type CustomEventHandler = (e: CustomEvent) => void;

/** The root component taking the whole canvas available space */
export class CanvasRootComponent extends Component {

	private invalidateHandler: InvalidateHandler;
	public ctx: CanvasRenderingContext2D;
	public env: FormEnvironment;
	public style: CSSStyleDeclaration;
	public dirtyLayout:boolean;
	public ratio: number;
	private lastLayoutProps:DrawingContextProperties;
	private animationEngine: AnimationEngine;
	private eventHandler: CustomEventHandler;
	private pointerCapture:Component[]|null;
	private pointerId: number;	// id of pointer capture


	constructor(canvas:HTMLCanvasElement, env: FormEnvironment, width: number, height: number, ratio: number) {
		super();
		this.ctx = canvas.getContext('2d');
		this.env = env;
		this.style = getComputedStyle(canvas);
		this.x = 0;
		this.y = 0;
		this.width = width;
		this.height = height;
		this.ratio = ratio;
		this.dirtyLayout = true;
		this.lastLayoutProps = {};
		this.pointerCapture = null;
	}

	get canvas():HTMLCanvasElement {
		return this.ctx.canvas;
	}

	prepareDrawingContext(useEmptyProps:boolean):DrawingContext {
		if (useEmptyProps) this.lastLayoutProps = {};	// forget previous props
		return { ctx: this.ctx, env: this.env, style: this.style, props: this.lastLayoutProps };
	}


	setInvalidateHandler(invalidateHandler:InvalidateHandler) {
		this.invalidateHandler = invalidateHandler;
	}

	setEventHandler(ehandler:CustomEventHandler) {
		this.eventHandler = ehandler;
	}

	dirty(layout: boolean) {
		this.dirtyLayout = this.dirtyLayout || layout;
		this.invalidateHandler?.();
	}

	/** Change dimensions to this values */
	resizeTo(width: number, height: number) {
		this.width = width;
		this.height = height;
	}

	/** Setting screen density */
	setRatio(ratio: number) {
		this.ratio = ratio;
	}

	/** Destroy this canvas root */
	destroy() {
	}

	refresh() {
		const dc = this.prepareDrawingContext(this.dirtyLayout);
		try {
			dc.ctx.save();
			// Scale the context to ensure content is drawn correctly
			dc.ctx.scale(this.ratio, this.ratio);
			if (this.dirtyLayout) {
				this.layout(dc);	// calculate layout
				this.dirtyLayout = false;
			}
			this.draw(dc, {x: this.x, y: this.y, width: this.width, height: this.height});	// draw content
		} finally {
			dc.ctx.restore();
		}

	}


	protected drawBackground(dc: DrawingContext, area:Rect) {
		dc.ctx.clearRect(this.x, this.y, this.width, this.height);
	}

	setAnimationEngine(engine:AnimationEngine) {
		this.animationEngine = engine;
	}

	animate(a: Animation): AnimationID {
		if (this.animationEngine) return this.animationEngine.add(a);
		return 0;
	}

	cancelAnimation(id: AnimationID) {
		if (this.animationEngine) return this.animationEngine.cancel(id);
	}


	onEvent(e: CustomEvent): boolean {
		this.eventHandler?.(e);
		return false;
	}

	protected rememberPointerCapture(e: PointerEvent, target: Component) {
		const path:Component[] = [];
		while (target != null) {
			path.splice(0, 0, target);
			target = target.parent;
		}
		this.pointerCapture = path;
		this.ctx.canvas.setPointerCapture(e.pointerId);	// set capture on the canvas
		this.pointerId = e.pointerId;
	}

	releasePointerCapture() {
		this.pointerCapture = null;
		if (this.pointerId) {
			this.ctx.canvas.releasePointerCapture(this.pointerId);
			this.pointerId = 0;
		}
	}


	/**
	 * Overriding the findTarget method to support pointer capture. If there
	 * is a capture it must be always returned
	 * @param x
	 * @param y
	 */
	findTarget(x: number, y: number): TargetPosition[] {
		if (this.pointerCapture) {
			const tps:TargetPosition[] = [];
			let coords:Coordinates;
			let parent:Component;
			for (let i = 0; i < this.pointerCapture.length; i++) {
				let c = this.pointerCapture[i];
				if (i === 0) coords = { x, y };
				else coords = parent.parentToChild(c, coords.x, coords.y);
				tps.push({ target:c, x: coords.x, y: coords.y });
				parent = c;
			}
			return tps;
		} else {
			return super.findTarget(x, y);
		}
	}
}

export interface Root {
	root: Ref<CanvasRootComponent|null>;
	width: Ref<number>,
	height: Ref<number>,
	canvasStyle: ComputedRef<CSSProperties>,
	wheel: (e:WheelEvent) => void,
	pointerMove: (e:PointerEvent) => void,
	pointerEnter: (e:PointerEvent) => void,
	pointerLeave: (e:PointerEvent) => void,
	pointerUp: (e:PointerEvent) => void,
	pointerDown: (e:PointerEvent) => void,
	touchStart: (e:TouchEvent) => void,
	touchMove: (e:TouchEvent) => void,
	touchEnd: (e:TouchEvent) => void,
	click: (e:MouseEvent) => void,
	dragStart: (e:DragEvent) => void,
	dragEnd: (e:DragEvent) => void,
	drag: (e:DragEvent) => void,
	dragEnter: (e:DragEvent) => void,
	dragLeave: (e:DragEvent) => void,
	dragOver: (e:DragEvent) => void,
	drop: (e:DragEvent) => void,
	dirty: Ref<DirtyFunction|null>
}

export interface DimensionProps {
	logicalWidth:number;
	logicalHeight:number;
}

export function useRoot(canvas:Ref<HTMLCanvasElement|null>, props:DimensionProps, env:FormEnvironment):Root {

	const root:Ref<CanvasRootComponent | null> = ref(null);
	const ratio = ref<number>(window.devicePixelRatio);

	// Physical width x height of the canvas
	const width = ref<number>(props.logicalWidth * ratio.value);
	const height = ref<number>(props.logicalHeight * ratio.value);
	let watchWidthHeightStop:WatchStopHandle = null;
	let watchLogicalWidthHeightStop:WatchStopHandle = null;
	let removeMediaListener = null;
	let animationEngine: AnimationEngine|null = null;
	const cursor = ref<string | null>(null);
	const dirty = ref<DirtyFunction|null>(null);

	watch(canvas, (c) => {
		if (c) {
			root.value = new CanvasRootComponent(c, env, props.logicalWidth, props.logicalHeight, ratio.value);

			// Refresh function
			const refresh = () => {
				let result:AnimateResult = { needNextFrame: false, needLayout: false };
				if (animationEngine) {
					result = animationEngine.animateRunning();
				}
				if (result.needLayout) root.value.dirtyLayout = true;
				root.value.refresh();
				if (result.needNextFrame) window.requestAnimationFrame(refresh);
			};
			dirty.value = refresh;
			//const refresh = root.value.refresh.bind(root.value);
			animationEngine = new AnimationEngine(refresh);
			root.value.setAnimationEngine(animationEngine);
			// root.value.setEventHandler((e:CustomEvent) => {
			// });

			// Refresh canvas when internal state changes
			root.value.setInvalidateHandler(() => {
				window.requestAnimationFrame(refresh);
			});

			// Refresh canvas when dimension changes
			watchWidthHeightStop = watch([width, height], () => {
				root.value.dirtyLayout = true;	// always re-layout if dimensions change
				window.requestAnimationFrame(refresh);
			});

			let recalcTimeout = 0;
			const RECALC_IF_NO_CHANGE = 50;

			function reallyRecalcDimensions():void {
				width.value = props.logicalWidth * ratio.value;
				height.value = props.logicalHeight * ratio.value;
				root.value.resizeTo(props.logicalWidth, props.logicalHeight);
				recalcTimeout = 0;
			}

			function recalcDimensions():void {
				if (recalcTimeout) clearTimeout(recalcTimeout);
				recalcTimeout = setTimeout(reallyRecalcDimensions, RECALC_IF_NO_CHANGE);
			}

			watchLogicalWidthHeightStop = watch([() => props.logicalWidth, () => props.logicalHeight], recalcDimensions);

			const updatePixelRatio = () => {
				removeMediaListener?.();
				const mq = `(resolution: ${window.devicePixelRatio}dppx)`;
				const media = matchMedia(mq);
				media.addEventListener("change", updatePixelRatio);
				removeMediaListener = () => { media.removeEventListener("change", updatePixelRatio); };
				if (ratio.value !== window.devicePixelRatio) {
					ratio.value = window.devicePixelRatio;
					root.value.setRatio(ratio.value);
					reallyRecalcDimensions();
				}
			};
			updatePixelRatio();

		} else {
			watchLogicalWidthHeightStop?.();
			watchWidthHeightStop?.();
			removeMediaListener?.();
			animationEngine?.cancel();

			if (root.value != null) {
				root.value.destroy();
				root.value = null;
			}
			dirty.value = null;
		}
	});

	const canvasStyle = computed<CSSProperties>(() => {
		const s:CSSProperties = {};
		s.width = props.logicalWidth + "px";
		s.height = props.logicalHeight + "px";
		if (cursor.value) s.cursor = cursor.value;
		return s;
	});

	function comparePaths(oldp:TargetPosition[], newp:TargetPosition[]): { added: TargetPosition[], removed: TargetPosition[], same: TargetPosition[] } {
		let added: TargetPosition[] = [], removed: TargetPosition[] = [], same: TargetPosition[] = [];
		const maxLen = Math.max(oldp.length, newp.length);
		let sameItem = true;
		for (let i = 0; i < maxLen; i++) {
			const o = i < oldp.length ? oldp[i] : null;
			const n = i < newp.length ? newp[i] : null;
			if (sameItem) {
				if (o != null && n != null && o.target === n.target) {
					same.push(n);
				} else {
					if (o != null) removed.push(o);
					if (n != null) added.push(n);
					sameItem = false;
				}
			} else {
				if (o != null) removed.push(o);
				if (n != null) added.push(n);
			}
		}
		return {added, removed, same };
	}

	function bubbleEvent<T extends MouseEvent|TouchEvent>(tps:TargetPosition[], e:T, caller: (e:T, tp:TargetPosition) => boolean):void {
		let continueBubbling = true;
		for (let i = tps.length - 1; continueBubbling && i >= 0; i--) {
			const tp = tps[i];
			continueBubbling = caller(e, tp);
		}
	}

	function targetPosition<T extends MouseEvent>(e:T):TargetPosition[] {
		if (root.value) {
			const coord = localCoord(e, canvas.value);
			return root.value.findTarget(coord.x, coord.y);
		}
		return [];
	}

	function processEvent<T extends MouseEvent|TouchEvent>(e:T, caller: (e:T, tp:TargetPosition) => boolean, preventDefault = false):TargetPosition[] {
		if (root.value) {
			if (preventDefault) e.preventDefault();
			const coord = localCoord(e, canvas.value);
			let tps = root.value.findTarget(coord.x, coord.y);
			bubbleEvent(tps, e, caller);
			return tps;
		}
		return [];
	}

	let lastPointerMovePath:TargetPosition[] = [];
	let lastDragOverPath:TargetPosition[] = [];
	let lastDragStartPath:TargetPosition[] = [];

	function applyCursor(tps:TargetPosition[]):string {
		let cr = null;
		for (let tp of tps) {
			const compCursor = tp.target.getCursor(tp.x, tp.y);
			if (compCursor != null) cr = compCursor;
		}
		cursor.value = cr;
		return cr;
	}

	function wheelScroll(e:WheelEvent):void {
		const tps = processEvent(e, (e:MouseEvent, tp:TargetPosition) => tp.target.onWheel(e as WheelEvent, tp.x, tp.y));
		applyCursor(tps);
	}

	function pointerMove(e:PointerEvent):void {
		const tps = targetPosition(e);
		const changes = comparePaths(lastPointerMovePath, tps);
		bubbleEvent(changes.removed, e, (e:PointerEvent, tp:TargetPosition) => tp.target.onPointerLeave(e, tp.x, tp.y));
		bubbleEvent(changes.added, e, (e:PointerEvent, tp:TargetPosition) => tp.target.onPointerEnter(e, tp.x, tp.y));
		bubbleEvent(tps, e, (e:PointerEvent, tp:TargetPosition) => tp.target.onPointerMove(e, tp.x, tp.y));
		lastPointerMovePath = tps;
		applyCursor(tps);
	}

	function pointerEnter(e:PointerEvent):void {
		const tps = processEvent(e, (e:PointerEvent, tp:TargetPosition) => tp.target.onPointerEnter(e, tp.x, tp.y));
		lastPointerMovePath = tps;
		applyCursor(tps);
	}

	function pointerLeave(e:PointerEvent):void {
		bubbleEvent(lastPointerMovePath, e, (e:PointerEvent, tp:TargetPosition) => tp.target.onPointerLeave(e, tp.x, tp.y));
		lastPointerMovePath = [];
	}

	function click(e:MouseEvent):void {
		processEvent(e, (e:MouseEvent, tp:TargetPosition) => tp.target.onClick(e, tp.x, tp.y));
	}

	function pointerUp(e:PointerEvent):void {
		processEvent(e, (e:PointerEvent, tp:TargetPosition) => tp.target.onPointerUp(e, tp.x, tp.y));
		if (root.value) root.value.releasePointerCapture();
	}

	function pointerDown(e:PointerEvent):void {
		processEvent(e, (e:PointerEvent, tp:TargetPosition) => tp.target.onPointerDown(e, tp.x, tp.y));
	}

	function dragStart(e:DragEvent):void {
		const tps = processEvent(e, (e:DragEvent, tp:TargetPosition) => tp.target.onDragStart(e, tp.x, tp.y));
		lastDragStartPath = tps;
		applyCursor(tps);
	}

	function dragEnd(e:DragEvent):void {
		// Drag end must be delivered to the component initiating dragstart
		bubbleEvent(lastDragStartPath, e, (e:DragEvent, tp:TargetPosition) => tp.target.onDragEnd(e, tp.x, tp.y));
		lastDragStartPath = [];
	}

	function drag(e:DragEvent):void {
		// Drag end must be delivered to the component initiating dragstart
		bubbleEvent(lastDragStartPath, e, (e:DragEvent, tp:TargetPosition) => tp.target.onDrag(e, tp.x, tp.y));
	}

	function dragEnter(e:DragEvent):void {
		const tps = processEvent(e, (e:DragEvent, tp:TargetPosition) => tp.target.onDragEnter(e, tp.x, tp.y));
		lastDragOverPath = tps;
		applyCursor(tps);
	}

	function dragLeave(e:DragEvent):void {
		bubbleEvent(lastDragOverPath, e, (e:DragEvent, tp:TargetPosition) => tp.target.onDragLeave(e, tp.x, tp.y));
		lastDragOverPath = [];
	}

	function drop(e:DragEvent):void {
		const tps = processEvent(e, (e:DragEvent, tp:TargetPosition) => tp.target.onDrop(e, tp.x, tp.y));
		applyCursor(tps);
	}

	function dragOver(e:DragEvent):void {
		const tps = targetPosition(e);
		const changes = comparePaths(lastDragOverPath, tps);
		bubbleEvent(changes.removed, e, (e:DragEvent, tp:TargetPosition) => tp.target.onDragLeave(e, tp.x, tp.y));
		bubbleEvent(changes.added, e, (e:DragEvent, tp:TargetPosition) => tp.target.onDragEnter(e, tp.x, tp.y));
		bubbleEvent(tps, e, (e:DragEvent, tp:TargetPosition) => tp.target.onDragOver(e, tp.x, tp.y));
		lastDragOverPath = tps;
		applyCursor(tps);
	}

	function touchStart(e:TouchEvent):void {
		processEvent(e, (e:TouchEvent, tp:TargetPosition) => tp.target.onTouchStart(e, tp.x, tp.y));
	}

	function touchMove(e:TouchEvent):void {
		processEvent(e, (e:TouchEvent, tp:TargetPosition) => tp.target.onTouchMove(e, tp.x, tp.y));
	}

	function touchEnd(e:TouchEvent):void {
		processEvent(e, (e:TouchEvent, tp:TargetPosition) => tp.target.onTouchEnd(e, tp.x, tp.y));
	}

	return {
		root,
		width,
		height,
		canvasStyle,
		wheel: wheelScroll,
		pointerMove,
		pointerEnter,
		pointerLeave,
		pointerUp,
		pointerDown,
		click,
		dragStart,
		dragEnd,
		drag,
		dragEnter,
		dragLeave,
		dragOver,
		drop,
		touchStart,
		touchMove,
		touchEnd,
		dirty
	}
}